import { FakeCaretInput, type FakeCaretInputRef } from '@/components/common/fake-caret-input';
import KeyboardSimple from '@/components/common/keyboard-simple';
import ShopFnbClearButton from '@/components/common/shop-fnb-facility/buttons/clear';
import { BuildingNameType } from '@/enums/building';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import { useAccessibilityTTS, useOrientation } from '@/hooks';
import { useClickOutsideDetector } from '@/hooks/useClickOutsideDetector';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore, useFnbStore } from '@/stores';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

const FnbSearchByText = () => {
    const { isHorizontal } = useOrientation();
    const { t } = useTranslation();
    const { speak } = useAccessibilityTTS();

    // Optimized store access
    const searchText = useFnbStore((state) => state.searchText);
    const keyboardLanguage = useFnbStore((state) => state.keyboardLanguage);
    const setSearchText = useFnbStore((state) => state.setSearchText);
    const setKeyboardLanguage = useFnbStore((state) => state.setKeyboardLanguage);
    const updateFnbContents = useFnbStore((state) => state.updateFnbContents);
    const buildingType = useConfigStore((state) => state.config.server?.building?.buildingType);
    const language = useConfigStore((state) => state.config.ui.language);

    const [tempSearchText, setTempSearchText] = useState(searchText);

    const [isCaretVisible, setIsCaretVisible] = useState(false);

    const virtualKeyboardInputRef = useRef<FakeCaretInputRef>(null);
    const inputWrapperRef = useRef<HTMLDivElement>(null);
    const fakeCaretInputWrapperRef = useRef<HTMLDivElement>(null);

    useClickOutsideDetector(inputWrapperRef, () => {
        setIsCaretVisible(false);
    });

    useEffect(() => {
        setTempSearchText(searchText);
    }, [searchText]);

    useEffect(() => {
        if (language === 'ko') {
            setKeyboardLanguage('ko');
        } else {
            setKeyboardLanguage('en');
        }
    }, [language, setKeyboardLanguage]);

    const handleClear = () => {
        setTempSearchText('');
        setSearchText('');
        updateFnbContents(SHOP_SEARCH_MODE.SEARCH_BY_TEXT);
        setTimeout(() => {
            fakeCaretInputWrapperRef.current?.focus();
            setIsCaretVisible(true);
        }, 0);
    };

    const handleInputChange = (value: string) => {
        if (!tempSearchText && value.length === 0) return;
        setTempSearchText(value);
        if (value.length === 0) {
            setSearchText('');
            updateFnbContents(SHOP_SEARCH_MODE.SEARCH_BY_TEXT);
        }
    };

    const handleSearchContents = (searchText: string) => {
        setSearchText(searchText);
        const results = updateFnbContents(SHOP_SEARCH_MODE.SEARCH_BY_TEXT);

        setTimeout(() => {
            if (results?.length === 0) {
                const emptyElement = document.getElementById('fnb-empty-result');
                if (emptyElement) {
                    emptyElement.blur();
                    emptyElement.focus();
                }
            } else {
                speak(t('shop.search.tts.content.search_result'));
                const pageCountElement = document.getElementById('fnb-page-count');
                if (pageCountElement) {
                    pageCountElement.blur();
                    pageCountElement.focus();
                }
            }
        }, 500);

        if (searchText?.trim()) {
            TrackingService.trackKeyword(searchText.trim());
        }
    };

    return (
        <div
            className={clsx(styles['search-text'], {
                [styles['search-text__horizontal']]: isHorizontal,
                [styles['search-text__vertical']]: !isHorizontal,
            })}
        >
            <div ref={inputWrapperRef} className={styles['search-text__input']} data-region-nav>
                <div
                    ref={fakeCaretInputWrapperRef}
                    className={styles['search-text__input--input-wrapper']}
                    tabIndex={9}
                    data-interactive
                    aria-label={`${t('common.keyboard.tts.search_readonly_area')} ${tempSearchText ? tempSearchText : t('fnb.search.text.content.input_placeholder')}`}
                >
                    <FakeCaretInput
                        fullWidth={true}
                        ref={virtualKeyboardInputRef}
                        isCaretVisible={isCaretVisible}
                        tabIndex={-1}
                        type="text"
                        placeholder={t('fnb.search.text.content.input_placeholder')}
                        value={tempSearchText}
                        onFocus={() => {
                            setIsCaretVisible(true);
                        }}
                    />
                </div>
                <div className={styles['search-text__input--clear-button-wrapper']}>
                    {tempSearchText && (
                        <ShopFnbClearButton
                            data-tts-scope="keyboard"
                            onFocus={() => {
                                speak(`${t('fnb.search.tts.content.delete_all_input_value')}, ${t('common.button.tts.button')}`);
                            }}
                            tabIndex={9}
                            role="button"
                            data-interactive
                            aria-label={t('fnb.search.tts.content.delete_all_input_value')}
                            onClick={() => {
                                handleClear();
                            }}
                            className={styles['search-text__input--clear-button']}
                            disabled={!tempSearchText}
                        />
                    )}
                </div>
            </div>
            <KeyboardSimple
                tabIndex={9}
                value={tempSearchText}
                onInputChange={(value) => {
                    handleInputChange(value);
                    setIsCaretVisible(true);
                    virtualKeyboardInputRef.current?.updateCaretPosition();
                }}
                onSearch={() => handleSearchContents(tempSearchText)}
                keyboardLanguage={keyboardLanguage}
                onLanguageChange={setKeyboardLanguage}
                buildingType={buildingType || BuildingNameType.MALL}
            />
        </div>
    );
};

export default FnbSearchByText;
